import { useOrganization } from '@/contexts/OrganizationContext';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { FEATURE_FLAG_KEYS } from '@/lib/featureFlagsCatalog';

/** `resolved` = the server answered (on or off); `error` = it could not be resolved (unknown). */
export type RuntimeAdvisoriesFlagStatus = 'loading' | 'resolved' | 'error';

export interface RuntimeAdvisoriesFlagState {
  /** True only once the server resolved the flag ON for this organisation. */
  enabled: boolean;
  /** True while the organisation or the flag is still resolving: render nothing, never redirect. */
  loading: boolean;
  /**
   * Tri-state for callers that must not treat unknown as off (PR #3654 review r11): an error is
   * never a resolved "off". No organisation is a resolved off (nothing to resolve).
   */
  status: RuntimeAdvisoriesFlagStatus;
  /** Re-resolve after an error. */
  retry: () => void;
}

/**
 * ENT-5889 (Feature flag section): the org-targeted `runtime_advisories` exposure switch,
 * resolved server-side (feature-flags-resolve) for ONE organisation. Fail closed: loading or an
 * error is never "on", and no organisation is "off". Defaults to the current organisation; the
 * vendor portal passes its claimant organisation.
 */
export function useRuntimeAdvisoriesFlag(organizationId?: string | null): RuntimeAdvisoriesFlagState {
  const { organization, user, isLoading: organizationLoading } = useOrganization();
  const explicit = organizationId !== undefined;
  const orgId = explicit ? organizationId : organization?.id ?? null;
  const access = useFeatureAccess(FEATURE_FLAG_KEYS.runtimeAdvisories, { organizationId: orgId, enabled: Boolean(orgId) });
  const retry = () => {
    void access.refetch();
  };

  if (!explicit && organizationLoading) return { enabled: false, loading: true, status: 'loading', retry };
  if (!orgId || !user?.id) return { enabled: false, loading: false, status: 'resolved', retry };
  if (access.isError) return { enabled: false, loading: false, status: 'error', retry };
  if (!access.isSuccess) return { enabled: false, loading: true, status: 'loading', retry };
  return { enabled: access.enabled === true, loading: false, status: 'resolved', retry };
}
